import './styles/NetworkInfo.scss';

import { Alert } from '@whistler-ui/k2/components/Alert';
import { CodeBlock } from '@whistler-ui/k2/components/CodeBlock';
import { CopyButton } from '@whistler-ui/k2/components/CopyButton';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { TruncateText } from '@whistler-ui/k2/components/TruncateText';
import { Paragraph } from '@whistler-ui/k2/components/Typography';
import { formatDuration } from '@whistler-ui/k2/helpers';
import { useClassHelper } from '@whistler-ui/k2/hooks/useClassHelper';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SmallAiButton } from '../../modules/AI/EntitlementAssistant/components/SmallAiButton';
import { ILogsError } from '../../types';
import { MoreHeaders } from './components/MoreHeaders';
import {
  General,
  Ids,
  RenderHeader,
  RequestPayload,
  ResponseBody,
} from './types';

const componentName = 'c-NetworkInfo';

interface Props {
  general?: General;
  request?: RequestPayload;
  response?: ResponseBody;
  ids?: Ids;
  error?: ILogsError;
  fixedNetworkHeight?: boolean;
  onSendMessageToAi?: (message: string) => void;
}

export const NetworkInfo = ({
  general,
  request,
  response,
  ids,
  error,
  fixedNetworkHeight = false,
  onSendMessageToAi,
}: Props) => {
  const c = useClassHelper(componentName);
  const { t } = useTranslation();

  const apiCopy = useMemo(() => {
    return JSON.stringify(
      {
        ...general,
        request,
        response,
        error,
      },
      null,
      2,
    );
  }, [error, general, request, response]);

  const renderHeaders = (options: RenderHeader) => {
    return (
      <Fragment>
        <h3 className={c('__title')}>{options.title}</h3>
        <div className="inner">
          {options.tId && (
            <p className={c('__text')}>
              <b>{t('componentTestConsolePane_transaction_id')}</b>{' '}
              <span data-testid="n-tId">{options.tId}</span>
            </p>
          )}
          {options.cId && (
            <p className={c('__text')}>
              <b>{t('componentTestConsolePane_correlation_id')}</b>{' '}
              <span data-testid="n-cId">{options.cId}</span>
            </p>
          )}
          {options.eId && (
            <p className={c('__text')}>
              <b>{t('componentTestConsolePane_environment_id')}</b>{' '}
              <span data-testid="n-eId">{options.eId}</span>
            </p>
          )}
        </div>
      </Fragment>
    );
  };

  const renderEmptyRequestResponse = (text: string) => {
    return (
      <Alert variant="default" isRounded={false} className={c('__empty-json')}>
        <Paragraph className={c('__empty-json-text')} isBolder>
          {text}
        </Paragraph>
      </Alert>
    );
  };

  const renderRequest = () => {
    if (!request?.json) {
      return renderEmptyRequestResponse('Request body is empty');
    }

    return Array.isArray(request.json) ? (
      request.json.map((json, index) => {
        return (
          <CodeBlock
            testId={`n-json-request-${index}`}
            key={`request-json-${index}`}
            className={c(
              '__json-code',
              fixedNetworkHeight ? '--fixed-height' : '',
            )}
            obj={json}
          />
        );
      })
    ) : (
      <CodeBlock
        testId="n-json-request"
        className={c('__json-code')}
        obj={request.json}
      />
    );
  };

  const renderResponse = () => {
    if (!response?.json && !error) {
      return renderEmptyRequestResponse('Response body is empty');
    }

    if (Array.isArray(response.json)) {
      return (
        <Fragment>
          {response.json.map((json, index) => {
            return (
              <CodeBlock
                testId={`n-json-response-${index}`}
                key={`request-json-${index}`}
                className={c(
                  '__json-code',
                  fixedNetworkHeight ? '--fixed-height' : '',
                )}
                obj={json}
              />
            );
          })}
        </Fragment>
      );
    }

    return (
      <Fragment>
        <CodeBlock
          testId="n-json-response"
          className={c('__json-code')}
          obj={response.json}
          errorAttributes={
            error ? error.errors?.map(item => item.attribute) : undefined
          }
        />
        {error && (
          <CodeBlock
            testId="n-json-response-error"
            className={c('__json-code')}
            obj={error}
            variant="danger"
          />
        )}
      </Fragment>
    );
  };

  const totalTime = formatDuration(general?.responseTimeMs, {
    showAsAPlanDecimals: true,
  });

  return (
    <section className={c(componentName)} data-testid="n-wrapper">
      {general && (
        <Stack
          tag="header"
          direction="row"
          gap={2}
          align="center"
          justify="between"
          className={`${c('__header')} ${
            general.spaceBetween ? 'with-spacing' : ''
          }`}
          data-testid="n-header"
        >
          <TruncateText showMoreButton showLessButton>
            <Paragraph tag="span" isBolder data-testid="n-method">
              {general?.method ?? '-'}:
            </Paragraph>{' '}
            <span
              data-testid="n-url"
              title={general?.url ? general?.url : undefined}
            >
              {general?.url ?? '-'}
            </span>
          </TruncateText>
          <div className={c('__header-right')}>
            <Stack direction="row" gap={4} align="center">
              <Stack direction="row" className="gap-[5px]!">
                <Paragraph isBolder>Status:</Paragraph>
                <Paragraph data-testid="n-status-code">
                  {general?.statusCode ?? '-'}
                </Paragraph>
              </Stack>
              <Stack direction="row" className="gap-[5px]!">
                <Paragraph isBolder>Time (ms):</Paragraph>
                <Paragraph data-testid="n-response-time">
                  {totalTime ? `${totalTime}` : '-'}
                </Paragraph>
              </Stack>
              <Stack direction="row" gap={1}>
                <CopyButton.Wrapper
                  toolTipAlign="left"
                  label={t('testEnvironmentTab_copy_all')}
                  value={apiCopy}
                  variant="row-header"
                  withToolTip
                />

                <SmallAiButton
                  onClick={onSendMessageToAi}
                  correlationId={ids?.cId}
                />
              </Stack>
            </Stack>
          </div>
        </Stack>
      )}

      <section className={c('__container')}>
        <div className={c('__row')}>
          <div className={c('__body')}>
            <span data-testid="n-request-ids">
              {ids &&
                renderHeaders({
                  title: t('componentTestConsolePane_request_headers'),
                  tId: ids.tId,
                  cId: ids.cId,
                  eId: ids.eId,
                })}
            </span>
            {request && (
              <MoreHeaders
                headers={request.headers}
                componentName={componentName}
                testId="n-request-more"
              />
            )}
          </div>
          <div className={c('__body')}>
            <span data-testid="n-response-ids">
              {ids &&
                renderHeaders({
                  title: t('componentTestConsolePane_response_headers'),
                  tId: ids.tId,
                  cId: ids.cId,
                  eId: ids.eId,
                })}
            </span>
            {response && (
              <MoreHeaders
                headers={response.headers}
                componentName={componentName}
                testId="n-response-more"
              />
            )}
          </div>
        </div>

        {/* Request - Response JSON list */}
        <div className={c('__row')}>
          <div className={c('__body')} data-testid="n-render-request">
            {renderRequest()}
          </div>
          <div className={c('__body')} data-testid="n-render-response">
            {renderResponse()}
          </div>
        </div>
      </section>
    </section>
  );
};
